import { getDiscountedEventLevelPrice } from "../../eventPricing";
import type { EventPromotion } from "../../eventPricing";
import type {
  AssignedSeatSelection,
  AssignedSeatingMap,
  AssignedSeatingSeat,
  AssignedSeatingTicketType,
} from "./types";

export const getAssignedSeatTicketTypeColor = (_seat: AssignedSeatingSeat) =>
  undefined;

const ticketTypeForSeat = (
  seatMap: AssignedSeatingMap | null | undefined,
  seat: AssignedSeatingSeat
) =>
  seatMap?.ticketTypes.find(
    (ticketType) => ticketType.key === seat.ticketTypeKey
  );

const levelForTicketType = (ticketTypeKey: string, levels: any[]) =>
  levels.find((level) => level?.seatingTicketTypeKey === ticketTypeKey);

export const getAssignedSeatingTicketTypes = ({
  seatMap,
}: {
  seatMap?: AssignedSeatingMap | null;
}): AssignedSeatingTicketType[] => {
  const ticketTypes = new Map<string, AssignedSeatingTicketType>();
  (seatMap?.seats || []).forEach((seat) => {
    if (seat.type !== "seat" || !seat.ticketTypeKey) {
      return;
    }
    const metadata = ticketTypeForSeat(seatMap, seat);
    if (!metadata) {
      return;
    }
    const current = ticketTypes.get(seat.ticketTypeKey);
    ticketTypes.set(seat.ticketTypeKey, {
      key: seat.ticketTypeKey,
      name: metadata.name,
      color: metadata.color,
      price: metadata.price,
      availableSeats:
        (current?.availableSeats || 0) + (seat.state === "available" ? 1 : 0),
    });
  });
  return Array.from(ticketTypes.values());
};

export const getAssignedSeatingTicketTypeForLevel = ({
  level,
  ticketTypes,
}: {
  level: any;
  ticketTypes: AssignedSeatingTicketType[];
}) =>
  ticketTypes.find(
    (ticketType) => ticketType.key === level?.seatingTicketTypeKey
  );

export const isAssignedSeatingLevelSoldOut = ({
  level,
  ticketTypes,
}: {
  level: any;
  ticketTypes: AssignedSeatingTicketType[];
}) => {
  const ticketType = getAssignedSeatingTicketTypeForLevel({
    level,
    ticketTypes,
  });
  return !ticketType || ticketType.availableSeats <= 0;
};

export const areAssignedSeatingTicketTypesSoldOut = (
  ticketTypes: AssignedSeatingTicketType[]
) =>
  ticketTypes.length > 0 &&
  ticketTypes.every((ticketType) => ticketType.availableSeats <= 0);

export const formatAssignedSeatingSeatsLeft = (
  ticketTypes: AssignedSeatingTicketType[],
  translate: any
) =>
  `${ticketTypes.reduce(
    (total, ticketType) => total + ticketType.availableSeats,
    0
  )} ${translate("seatsLeft")} `;

export const getAvailableSeatKeys = (seatMap?: AssignedSeatingMap | null) =>
  new Set(
    (seatMap?.seats || [])
      .filter((seat) => seat.type === "seat" && seat.state === "available")
      .map((seat) => seat.seatKey)
  );

export const removeUnavailableAssignedSeats = ({
  seatMap,
  selectedSeatKeys,
}: {
  seatMap?: AssignedSeatingMap | null;
  selectedSeatKeys: string[];
}) => {
  if (!seatMap) {
    return selectedSeatKeys;
  }
  const availableSeatKeys = getAvailableSeatKeys(seatMap);
  return selectedSeatKeys.filter((seatKey) => availableSeatKeys.has(seatKey));
};

export const recoverAssignedSeatSelection = ({
  seatMap,
  selectedSeatKeys,
  unavailableSeatKeys = [],
}: {
  seatMap?: AssignedSeatingMap | null;
  selectedSeatKeys: string[];
  unavailableSeatKeys?: string[];
}) =>
  removeUnavailableAssignedSeats({ seatMap, selectedSeatKeys }).filter(
    (seatKey) => !unavailableSeatKeys.includes(seatKey)
  );

export const toggleAssignedSeat = ({
  seatKey,
  selectedSeatKeys,
  seatMap,
}: {
  seatKey: string;
  selectedSeatKeys: string[];
  seatMap?: AssignedSeatingMap | null;
}) => {
  if (selectedSeatKeys.includes(seatKey)) {
    return selectedSeatKeys.filter((key) => key !== seatKey);
  }
  return getAvailableSeatKeys(seatMap).has(seatKey)
    ? [...selectedSeatKeys, seatKey]
    : selectedSeatKeys;
};

export const getAssignedSeatPosition = (seat: AssignedSeatingSeat) => ({
  row: seat.customRowLabel ?? seat.row,
  number: seat.customLabel ?? seat.number,
  x: seat.x,
  y: seat.y,
});

export const selectedAssignedSeats = ({
  seatMap,
  selectedSeatKeys,
  levels,
}: {
  seatMap?: AssignedSeatingMap | null;
  selectedSeatKeys: string[];
  levels: any[];
}): AssignedSeatSelection[] => {
  const ticketTypes = getAssignedSeatingTicketTypes({ seatMap });
  return selectedSeatKeys.flatMap((seatKey) => {
    const seat = seatMap?.seats.find(
      (candidate) => candidate.seatKey === seatKey
    );
    if (!seat || seat.type !== "seat" || !seat.ticketTypeKey) {
      return [];
    }
    const level = levelForTicketType(seat.ticketTypeKey, levels);
    const ticketType = ticketTypes.find(
      (candidate) => candidate.key === seat.ticketTypeKey
    );
    if (!level?.levelid || !ticketType) {
      return [];
    }
    const position = getAssignedSeatPosition(seat);
    return [
      {
        seatKey: seat.seatKey,
        levelId: level.levelid,
        row: position.row,
        number: position.number,
        ticketType: ticketType.name || seat.ticketTypeKey,
        ...(ticketType.color ? { ticketTypeColor: ticketType.color } : {}),
        price: ticketType.price,
      },
    ];
  });
};

export const selectedAssignedSeatTotal = (seats: AssignedSeatSelection[]) =>
  seats.reduce((total, seat) => total + seat.price, 0);

export const getAssignedSeatPricingSummary = ({
  seats,
  promotion,
}: {
  seats: AssignedSeatSelection[];
  promotion?: EventPromotion;
}) => {
  const originalTotal = selectedAssignedSeatTotal(seats);
  const total =
    Math.round(
      seats.reduce(
        (sum, seat) =>
          sum +
          getDiscountedEventLevelPrice(
            { levelid: seat.levelId, price: seat.price },
            promotion
          ),
        0
      ) * 100
    ) / 100;
  const discount = Math.max(Math.round((originalTotal - total) * 100) / 100, 0);

  return { total, discount };
};
